
import React, { useEffect, useState } from 'react';
import { Tabs, List, Avatar, Badge, Typography } from 'antd';
import { TrophyOutlined, CalendarOutlined, StarOutlined } from '@ant-design/icons';
import Confetti from 'react-confetti';

const { Text } = Typography;

// Helper function to format date as "Month Day"
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Helper function to extract first name
const getFirstName = (fullName) => {
  if (!fullName) return '';
  // Remove titles (Mr., Ms., etc.) and suffixes (e.g., (STPL-0001))
  const cleanedName = fullName.replace(/^(Mr\.|Ms\.|Mrs\.)\s*/, '').replace(/\s*\([^)]+\)$/, '');
  const nameParts = cleanedName.trim().split(' ');
  // Return the first part as the first name
  return nameParts[0] || '';
};

// CSS styles for Work Anniversary
const workStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap');

  .work-card-container {
    perspective: 1000px;
    width: 100%;
    height: 60px;
  }

  .work-card {
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.6s;
    transform-style: preserve-3d;
  }

  .work-card:hover {
    transform: rotateY(180deg);
  }

  .work-card-front,
  .work-card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    display: flex;
    align-items: center;
    padding: 8px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    box-sizing: border-box;
  }

  .work-card-front {
    background: linear-gradient(to right, #e6f3ff, #fff9c4);
  }

  .work-card-back {
    background: linear-gradient(to right, #b3e5fc, #fadb14);
    transform: rotateY(180deg);
    justify-content: center;
    align-items: center;
    color: white;
    font-family: 'Dancing Script', cursive;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
    box-shadow: 0 0 10px rgba(250, 219, 20, 0.5);
  }

  .upcoming-card {
    transition: transform 0.3s ease;
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    padding: 8px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: linear-gradient(to right, #e6f3ff, #fff9c4);
  }

  .upcoming-card:hover {
    transform: scale(1.05);
  }

  .work-glow {
    animation: starGlow 1.5s ease-in-out infinite;
    box-shadow: 0 0 10px rgba(250, 219, 20, 0.5), 0 0 20px rgba(250, 219, 20, 0.3);
  }

  .shine {
    animation: shine 0.5s ease;
  }

  @keyframes starGlow {
    0%, 100% {
      box-shadow: 0 0 10px rgba(250, 219, 20, 0.5), 0 0 20px rgba(250, 219, 20, 0.3);
    }
    50% {
      box-shadow: 0 0 20px rgba(250, 219, 20, 0.8), 0 0 30px rgba(250, 219, 20, 0.5);
    }
  }

  @keyframes shine {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(10deg); }
    100% { transform: rotate(0deg); }
  }

  .trophy-badge {
    position: absolute;
    top: -10px;
    right: -10px;
    background: #fadb14;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    animation: sparkle 2s ease-in-out infinite;
  }

  @keyframes sparkle {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
  }
`;

// Inject styles only once
if (!document.getElementById('work-anniversary-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'work-anniversary-styles';
  styleSheet.innerText = workStyles;
  document.head.appendChild(styleSheet);
}

const WorkAnniversary = ({ WorkAnniversary }) => {
  const { TodayWorkAnniversary = [], UpcomingWorkAnniversary = [] } = WorkAnniversary || {};
  const [showConfetti, setShowConfetti] = useState(false);
  const [defaultActiveKey, setDefaultActiveKey] = useState('today');
  const today = formatDate(new Date());


  // Function to check if tab has data
  const hasData = (tabKey) => {
    switch (tabKey) {
      case 'today':
        return TodayWorkAnniversary && TodayWorkAnniversary.length > 0;
      case 'upcoming':
        return UpcomingWorkAnniversary && UpcomingWorkAnniversary.length > 0;
      default:
        return false;
    }
  };

  // Effect to determine the default active tab based on available data
  useEffect(() => {
    if (hasData('today')) {
      setDefaultActiveKey('today');
    } else if (hasData('upcoming')) {
      setDefaultActiveKey('upcoming');
    } else {
      setDefaultActiveKey('today');
    }
  }, [TodayWorkAnniversary, UpcomingWorkAnniversary]);

  // Check for today's anniversaries
  useEffect(() => {
    const hasAnniversaryToday = TodayWorkAnniversary.some(
      (person) => formatDate(person.JoinDate) === today
    );
    if (hasAnniversaryToday) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [TodayWorkAnniversary, today]);

  const items = [
    {
      key: 'today',
      label: (
        <div className="flex items-center gap-1">
          <TrophyOutlined style={{ color: '#fadb14' }} />
          <Text strong style={{ fontSize: '14px', color: '#1f1f1f' }}>
            Today
          </Text>
          <Badge
            count={TodayWorkAnniversary.length}
            style={{ backgroundColor: '#fadb14', fontSize: '10px', height: '16px', lineHeight: '16px' }}
          />
        </div>
      ),
      children: (
        <div className="p-2 relative">
          {showConfetti && (
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              numberOfPieces={100}
              recycle={false}
              gravity={0.15}
              colors={['#fadb14', '#1890ff', '#40c4ff', '#b3e5fc']}
            />
          )}
          <List
            grid={{ gutter: 8, column: 2, xs: 1, sm: 2 }}
            dataSource={TodayWorkAnniversary}
            locale={{ emptyText: 'No work anniversaries today 🏆' }}
            renderItem={(person) => (
              <List.Item>
                <div className="work-card-container">
                  <div
                    className={`work-card ${
                      formatDate(person.JoinDate) === today ? 'work-glow shine' : ''
                    }`}
                  >
                    <div className="work-card-front">
                      <Avatar
                        size={32}
                        style={{ backgroundColor: '#1890ff', marginRight: '8px' }}
                      >
                        {person.EmployeeName?.charAt(0)}
                      </Avatar>
                      <div>
                        <Text strong style={{ fontSize: '12px', color: '#1f1f1f' }} className="truncate">
                          {person.EmployeeName}
                        </Text>
                        <div className="flex items-center gap-1">
                          <StarOutlined style={{ color: '#fadb14', fontSize: '12px' }} />
                          <Text type="secondary" style={{ fontSize: '10px' }}>
                            {formatDate(person.JoinDate)}
                          </Text>
                        </div>
                      </div>
                      {formatDate(person.JoinDate) === today && (
                        <span className="trophy-badge">🏅</span>
                      )}
                    </div>
                    <div className="work-card-back">
                      Happy Work Anniversary {getFirstName(person.EmployeeName)}!
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      ),
    },
    {
      key: 'upcoming',
      label: (
        <div className="flex items-center gap-1">
          <CalendarOutlined style={{ color: '#1890ff' }} />
          <Text strong style={{ fontSize: '14px', color: '#1f1f1f' }}>
            Upcoming
          </Text>
          <Badge
            count={UpcomingWorkAnniversary.length}
            style={{ backgroundColor: '#1890ff', fontSize: '10px', height: '16px', lineHeight: '16px' }}
          />
        </div>
      ),
      children: (
        <div className="p-2">
          <List
            grid={{ gutter: 8, column: 2, xs: 1, sm: 2 }}
            dataSource={UpcomingWorkAnniversary}
            locale={{ emptyText: 'No upcoming work anniversaries 🌟' }}
            renderItem={(person) => (
              <List.Item>
                <div className="upcoming-card">
                  <Avatar
                    size={32}
                    style={{ backgroundColor: '#1890ff', marginRight: '8px' }}
                  >
                    {person.EmployeeName?.charAt(0)}
                  </Avatar>
                  <div>
                    <Text strong style={{ fontSize: '12px', color: '#1f1f1f' }} className="truncate">
                      {person.EmployeeName}
                    </Text>
                    <div className="flex items-center gap-1">
                      <CalendarOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        {formatDate(person.JoinDate)}
                      </Text>
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      ),
    },
  ];

  return (
    <div
      className="work-bar rounded-lg shadow-lg"
      style={{ border: '1px solid var(--bg-column)' }}
    >
      <div className="flex items-center justify-center gap-0 p-2 bg-gradient-to-r from-blue-50 to-yellow-50 rounded-t-lg">
        <TrophyOutlined style={{ color: '#fadb14', fontSize: '16px' }} />
        <Text strong style={{ color: '#1f1f1f', fontSize: '14px' }}>
          Work Anniversaries
        </Text>
      </div>
      <Tabs
        defaultActiveKey={defaultActiveKey}
        key={defaultActiveKey}
        items={items}
        tabBarStyle={{ margin: 0, padding: '0 12px', background: '#fafafa' }}
        animated={{ inkBar: true, tabPane: false }}
      />
    </div>
  );
};

export default WorkAnniversary;